import { useLazyQuery } from '@apollo/client';
import format from 'date-fns/format';
import React, { useEffect, useRef, useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Separator,
  Stack,
  Text,
  useToast,
} from '@eluve/components';
import { ResultOf, graphql } from '@eluve/graphql.tada';

export const swapAppointmentPreviewFragment = graphql(`
  fragment SwapAppointmentPreview on Appointments @_unmask {
    __typename
    id
    name
    startDate
    endDate
    status
    userId
    patientId
    patient {
      __typename
      id
      fullName
    }
    user {
      __typename
      id
      firstName
      lastName
      email
    }
    doctor_interaction {
      __typename
      appointmentId
      noteSignedAt
    }
    external_appointments_infos {
      __typename
      id
    }
    external_charts {
      __typename
      id
    }
    syncTags: tags(
      where: {
        tag: {
          tag: { _in: ["SystemMetadata.API_SYNC", "SystemMetadata.DOM_SYNC"] }
        }
      }
    ) {
      __typename
      id
    }
  }
`);

const getSwapAppointmentsPreviewQuery = graphql(
  `
    query getSwapAppointmentsPreview(
      $tenantId: uuid!
      $appointmentIdA: uuid!
      $appointmentIdB: uuid!
    ) {
      appointmentA: appointmentsByPk(tenantId: $tenantId, id: $appointmentIdA) {
        ...SwapAppointmentPreview
      }
      appointmentB: appointmentsByPk(tenantId: $tenantId, id: $appointmentIdB) {
        ...SwapAppointmentPreview
      }
    }
  `,
  [swapAppointmentPreviewFragment],
);

export type PreviewAppointment = NonNullable<
  ResultOf<typeof getSwapAppointmentsPreviewQuery>['appointmentA']
>;

const uuidRegex =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const getValidationErrors = (
  appointmentA: PreviewAppointment | null,
  appointmentB: PreviewAppointment | null,
): string[] => {
  const errors: string[] = [];

  if (!appointmentA) {
    errors.push('Appointment A was not found in this organization.');
  }
  if (!appointmentB) {
    errors.push('Appointment B was not found in this organization.');
  }
  if (!appointmentA || !appointmentB) {
    return errors;
  }

  if (appointmentA.userId !== appointmentB.userId) {
    errors.push('The two appointments belong to different owners.');
  }
  if (!appointmentA.patient || !appointmentB.patient) {
    errors.push('Both appointments must have a patient assigned.');
  } else if (appointmentA.patient.id === appointmentB.patient.id) {
    errors.push('Both appointments already have the same patient.');
  }
  for (const [label, appointment] of [
    ['Appointment A', appointmentA],
    ['Appointment B', appointmentB],
  ] as const) {
    if (appointment.status !== 'COMPLETED') {
      errors.push(`${label} is not completed (status: ${appointment.status}).`);
    }
    if (
      appointment.syncTags.length > 0 ||
      appointment.external_charts.length > 0
    ) {
      errors.push(
        `${label} already has a note synced to the EHR. The swap must be corrected in the EHR manually.`,
      );
    }
  }
  if (
    appointmentA.external_appointments_infos.length > 1 ||
    appointmentB.external_appointments_infos.length > 1 ||
    appointmentA.external_appointments_infos.length !==
      appointmentB.external_appointments_infos.length
  ) {
    errors.push(
      'The appointments must both have exactly one external EHR link, or both have none.',
    );
  }

  return errors;
};

export const isConfirmationMatch = (input: string, patientName: string) => {
  const requiredLength = Math.min(5, patientName.length);
  return (
    input.length >= requiredLength &&
    patientName.toLowerCase().startsWith(input.toLowerCase())
  );
};

const formatAppointmentDate = (value: string | null) =>
  value
    ? `${format(new Date(value), 'PP')} ${format(new Date(value), 'h:mm a')}`
    : 'Not specified';

const AppointmentPreviewCard: React.FC<{
  appointment: PreviewAppointment;
}> = ({ appointment }) => {
  const isSigned = Boolean(appointment.doctor_interaction?.noteSignedAt);
  const ownerName = [appointment.user?.firstName, appointment.user?.lastName]
    .filter(Boolean)
    .join(' ');

  return (
    <Stack
      gap={0.5}
      className="rounded-lg border border-border-primary px-3 py-2 shadow-xs"
    >
      <Stack direction="horizontal" gap={2} align="center">
        <Text>{appointment.name || 'Untitled appointment'}</Text>
        {isSigned && <Badge variant="warningSubtle">Signed note</Badge>}
        {appointment.external_appointments_infos.length > 0 && (
          <Badge variant="secondary">EHR linked</Badge>
        )}
      </Stack>
      <Text color="tertiary">{`${ownerName} (${appointment.user?.email})`}</Text>
      <Text color="tertiary">
        {appointment.patient?.fullName ?? 'Unknown patient'}
      </Text>
      <Text color="tertiary">
        {formatAppointmentDate(appointment.startDate)} -{' '}
        {formatAppointmentDate(appointment.endDate)}
      </Text>
    </Stack>
  );
};

export interface SwapAppointmentPatientsDialogProps {
  tenantId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialAppointmentIdA?: string;
}

export const SwapAppointmentPatientsDialog: React.FC<
  SwapAppointmentPatientsDialogProps
> = ({ tenantId, open, onOpenChange, initialAppointmentIdA }) => {
  const toast = useToast();
  const apiClient = useApiClient();

  const [appointmentIdA, setAppointmentIdA] = useState(
    initialAppointmentIdA ?? '',
  );
  const [appointmentIdB, setAppointmentIdB] = useState('');
  const [confirmationA, setConfirmationA] = useState('');
  const [confirmationB, setConfirmationB] = useState('');
  const [isSwapping, setIsSwapping] = useState(false);
  const lastRequestedPairRef = useRef<string | null>(null);

  const [loadPreview, { data, loading, error }] = useLazyQuery(
    getSwapAppointmentsPreviewQuery,
    { fetchPolicy: 'network-only' },
  );

  useEffect(
    // Reset on hidden -> shown rather than on dismiss, so the dialog doesn't
    // shift layout while animating out.
    function resetStateWhenShown() {
      if (!open) {
        return;
      }
      setAppointmentIdA(initialAppointmentIdA ?? '');
      setAppointmentIdB('');
      setConfirmationA('');
      setConfirmationB('');
      lastRequestedPairRef.current = null;
    },
    [open, initialAppointmentIdA],
  );

  const trimmedIdA = appointmentIdA.trim();
  const trimmedIdB = appointmentIdB.trim();
  const currentPair = `${trimmedIdA}:${trimmedIdB}`;
  const idsAreValid =
    uuidRegex.test(trimmedIdA) &&
    uuidRegex.test(trimmedIdB) &&
    trimmedIdA !== trimmedIdB;

  useEffect(
    function autoLoadPreviewForValidIds() {
      if (!idsAreValid) {
        return;
      }
      const pair = `${trimmedIdA}:${trimmedIdB}`;
      if (lastRequestedPairRef.current === pair) {
        return;
      }
      lastRequestedPairRef.current = pair;
      setConfirmationA('');
      setConfirmationB('');
      void loadPreview({
        variables: {
          tenantId,
          appointmentIdA: trimmedIdA,
          appointmentIdB: trimmedIdB,
        },
      });
    },
    [idsAreValid, trimmedIdA, trimmedIdB, tenantId, loadPreview],
  );

  // The lazy query keeps its last result across state resets, so only treat
  // it as current when it belongs to the pair in the inputs.
  const previewIsCurrent =
    idsAreValid && lastRequestedPairRef.current === currentPair && !loading;
  const appointmentA = data?.appointmentA ?? null;
  const appointmentB = data?.appointmentB ?? null;
  const showPreview = previewIsCurrent && !error && Boolean(data);
  const showLoadError = previewIsCurrent && Boolean(error);
  const validationErrors = showPreview
    ? getValidationErrors(appointmentA, appointmentB)
    : [];
  const patientNameA = appointmentA?.patient?.fullName ?? '';
  const patientNameB = appointmentB?.patient?.fullName ?? '';
  const canSwap =
    showPreview &&
    validationErrors.length === 0 &&
    patientNameA !== '' &&
    patientNameB !== '';

  const confirmationsValid =
    canSwap &&
    isConfirmationMatch(confirmationA, patientNameA) &&
    isConfirmationMatch(confirmationB, patientNameB);

  const handleSwap = async () => {
    if (
      !confirmationsValid ||
      !appointmentA?.patient ||
      !appointmentB?.patient
    ) {
      return;
    }

    setIsSwapping(true);
    try {
      const result = await apiClient.adminAppointment.swapPatients({
        params: { tenantId },
        body: {
          appointmentIdA: appointmentA.id,
          appointmentIdB: appointmentB.id,
          expectedPatientIdA: appointmentA.patient.id,
          expectedPatientIdB: appointmentB.patient.id,
        },
      });

      if (result.status === 200) {
        toast.success('Patients swapped successfully');
        onOpenChange(false);
      } else if (result.status === 422) {
        toast.error(`Swap refused: ${result.body.message}`);
      } else {
        toast.error('Failed to swap patients');
      }
    } catch {
      toast.error('Failed to swap patients');
    } finally {
      setIsSwapping(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg" showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>Swap Appointments</DialogTitle>
          <DialogDescription>
            Exchanges the patients, names, and times between two appointments of
            the same owner. Each keep their own transcript and notes.
          </DialogDescription>
        </DialogHeader>

        <Stack gap={4}>
          <Stack gap={2}>
            <Label htmlFor="swap-appointment-id-a">Appointment A</Label>
            <Input
              id="swap-appointment-id-a"
              value={appointmentIdA}
              onChange={(e) => setAppointmentIdA(e.target.value)}
              placeholder="Paste the appointment ID"
              disabled={loading || isSwapping}
            />
            {showPreview && appointmentA && (
              <AppointmentPreviewCard appointment={appointmentA} />
            )}
          </Stack>

          <Stack gap={2}>
            <Label htmlFor="swap-appointment-id-b">Appointment B</Label>
            <Input
              id="swap-appointment-id-b"
              value={appointmentIdB}
              onChange={(e) => setAppointmentIdB(e.target.value)}
              placeholder="Paste the appointment ID"
              disabled={loading || isSwapping}
            />
            {showPreview && appointmentB && (
              <AppointmentPreviewCard appointment={appointmentB} />
            )}
          </Stack>

          {canSwap && (
            <>
              <Separator />
              <Stack gap={2}>
                <Text color="tertiary" weight="regular">
                  To confirm, type the patient names (at least the first 5
                  characters):
                </Text>
                <Input
                  value={confirmationA}
                  onChange={(e) => setConfirmationA(e.target.value)}
                  placeholder="Current patient of Appointment A"
                  aria-label="Confirm patient of Appointment A"
                />
                <Input
                  value={confirmationB}
                  onChange={(e) => setConfirmationB(e.target.value)}
                  placeholder="Current patient of Appointment B"
                  aria-label="Confirm patient of Appointment B"
                />
              </Stack>
            </>
          )}

          {(showLoadError || validationErrors.length > 0) && (
            <>
              <Separator />
              <Stack gap={1}>
                {showLoadError && (
                  <Text className="text-text-negative">
                    Failed to load appointment details.
                  </Text>
                )}
                {validationErrors.map((validationError) => (
                  <Text key={validationError} className="text-text-negative">
                    {validationError}
                  </Text>
                ))}
              </Stack>
            </>
          )}
        </Stack>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSwap}
            disabled={!confirmationsValid || isSwapping}
          >
            {isSwapping ? 'Swapping...' : 'Swap data'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
